import { useState, useEffect, useRef } from "react";
import { Play, Square, Sparkles, Plus, Trash2, Zap, Music, Loader2 } from "lucide-react";
import { ENTRY_TYPES, EXIT_TYPES, getAnimColor, isExit, runAnimations } from "./animEngine";
import { MOTION_PRESETS } from "./motionPresets";
import { analyzeAudio, buildBeatSyncAnimations } from "./audioAnalyzer";

/**
 * Animation Panel with interactive timeline drag-to-reposition.
 * Click a bar to select it; drag horizontally on the track to change start_at;
 * grab the right edge handle to resize duration.
 */
export default function AnimationPanel({ fabricRef, animations, setAnimations, duration, setDuration }) {
  const [selectedIdx, setSelectedIdx] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [selectedAnim, setSelectedAnim] = useState(null);
  const abortRef = useRef({ current: false });
  const trackRef = useRef(null);

  /* ---------- Selection sync ---------- */
  useEffect(() => {
    const c = fabricRef.current;
    if (!c) return;
    const initial = c.getActiveObject();
    if (initial) setSelectedIdx(c.getObjects().indexOf(initial));

    const sync = () => {
      const o = c.getActiveObject();
      if (!o) { setSelectedIdx(null); return; }
      setSelectedIdx(c.getObjects().indexOf(o));
    };
    c.on("selection:created", sync);
    c.on("selection:updated", sync);
    c.on("selection:cleared", () => setSelectedIdx(null));
    return () => {
      c.off("selection:created", sync);
      c.off("selection:updated", sync);
    };
  }, [fabricRef]);

  function addAnim(type) {
    if (selectedIdx == null) return;
    const next = [...animations, {
      object_index: selectedIdx, type,
      start_at: Math.min(duration - 500, animations.length * 260),
      duration: 600,
    }];
    setAnimations(next);
  }

  function applyPreset(preset) {
    const c = fabricRef.current;
    if (!c) return;
    const objs = c.getObjects();
    if (!objs.length) return;
    if (animations.length > 0) {
      const ok = window.confirm(`Replace your ${animations.length} existing animation${animations.length === 1 ? "" : "s"} with "${preset.label}"?`);
      if (!ok) return;
    }
    const nextAnims = preset.build(objs).filter(Boolean);
    setAnimations(nextAnims);
    setDuration(preset.duration_ms);
  }

  /* ---------- Music to Motion ---------- */
  const [audioMeta, setAudioMeta] = useState(null);
  const [audioLoading, setAudioLoading] = useState(false);
  const [audioErr, setAudioErr] = useState(null);
  async function onAudioFile(file) {
    if (!file) return;
    setAudioLoading(true); setAudioErr(null);
    try {
      const meta = await analyzeAudio(file);
      setAudioMeta(meta);
      const c = fabricRef.current;
      const objs = c?.getObjects() || [];
      if (objs.length) {
        const syncAnims = buildBeatSyncAnimations(objs, meta.beats, meta.duration_ms);
        if (animations.length && !window.confirm(`Replace ${animations.length} existing animations with beat-synced sequence?`)) return;
        setAnimations(syncAnims);
        // Clamp duration to a sensible 3-15s window regardless of full track length
        setDuration(Math.min(15000, Math.max(3000, meta.duration_ms)));
      }
    } catch (e) {
      setAudioErr(e?.message || "Couldn't analyze audio");
    } finally {
      setAudioLoading(false);
    }
  }

  function removeAnim(i) {
    setAnimations(animations.filter((_, idx) => idx !== i));
    if (selectedAnim === i) setSelectedAnim(null);
  }

  function updateAnim(i, patch) {
    setAnimations(animations.map((a, idx) => idx === i ? { ...a, ...patch } : a));
  }

  async function play() {
    const c = fabricRef.current;
    if (!c || playing) return;
    setPlaying(true);
    abortRef.current.current = false;
    await runAnimations(c, animations, duration, { abortRef: abortRef.current });
    setPlaying(false);
  }
  function stop() {
    abortRef.current.current = true;
    setPlaying(false);
  }

  /* ---------- Timeline drag ---------- */
  const drag = useRef({ idx: null, mode: null, startX: 0, origStart: 0, origDur: 0 });

  function onBarMouseDown(e, i, mode) {
    e.stopPropagation();
    const r = trackRef.current?.getBoundingClientRect();
    if (!r) return;
    drag.current = {
      idx: i, mode,
      startX: e.clientX,
      origStart: animations[i].start_at,
      origDur: animations[i].duration,
      trackW: r.width,
    };
    setSelectedAnim(i);
    window.addEventListener("mousemove", onDragMove);
    window.addEventListener("mouseup", onDragEnd);
  }
  function onDragMove(e) {
    const d = drag.current;
    if (d.idx == null) return;
    const deltaPx = e.clientX - d.startX;
    const deltaMs = Math.round((deltaPx / d.trackW) * duration);
    setAnimations((curr) => curr.map((a, idx) => {
      if (idx !== d.idx) return a;
      if (d.mode === "move") {
        const start = Math.max(0, Math.min(duration - a.duration, d.origStart + deltaMs));
        return { ...a, start_at: start };
      }
      // resize right edge
      const dur = Math.max(100, Math.min(duration - a.start_at, d.origDur + deltaMs));
      return { ...a, duration: dur };
    }));
  }
  function onDragEnd() {
    drag.current.idx = null;
    window.removeEventListener("mousemove", onDragMove);
    window.removeEventListener("mouseup", onDragEnd);
  }

  const c = fabricRef.current;
  const objCount = c ? c.getObjects().length : 0;

  return (
    <div data-testid="ads-anim-panel">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <div className="ads-panel-title" style={{ margin: 0 }}>Animations</div>
        {playing ? (
          <button className="ads-btn ads-btn-ghost" style={{ padding: "6px 10px", fontSize: 12 }} onClick={stop} data-testid="ads-anim-stop">
            <Square size={12} /> Stop
          </button>
        ) : (
          <button className="ads-btn ads-btn-primary" style={{ padding: "6px 10px", fontSize: 12 }} onClick={play} disabled={!animations.length} data-testid="ads-anim-play">
            <Play size={12} /> Preview
          </button>
        )}
      </div>

      {/* Music to Motion */}
      <div className="ads-panel-title" style={{ fontSize: 10, marginTop: 4, marginBottom: 8, display: "flex", alignItems: "center", gap: 6 }}>
        <Music size={11} /> Music to Motion — sync to a beat
      </div>
      <label
        htmlFor="ads-anim-audio-input"
        className="ads-music-drop"
        data-testid="ads-music-drop"
        style={{ opacity: (fabricRef.current?.getObjects()?.length ?? 0) === 0 ? 0.45 : 1 }}
      >
        {audioLoading ? (
          <><Loader2 size={14} className="animate-spin" /> Analyzing track…</>
        ) : audioMeta ? (
          <>
            <Music size={14} />
            <div style={{ fontWeight: 700, fontSize: 12 }}>{audioMeta.filename}</div>
            <div style={{ fontSize: 10, color: "var(--ads-text-dim)" }}>
              {audioMeta.bpm ? `${audioMeta.bpm} BPM · ` : ""}{audioMeta.count} beats · {(audioMeta.duration_ms / 1000).toFixed(1)}s
            </div>
          </>
        ) : (
          <>
            <Music size={14} />
            <div style={{ fontWeight: 600, fontSize: 12 }}>Drop an mp3 / wav to beat-sync</div>
            <div style={{ fontSize: 10, color: "var(--ads-text-dim)" }}>
              We detect the tempo and place animations on the beat.
            </div>
          </>
        )}
        <input
          id="ads-anim-audio-input"
          type="file"
          accept="audio/*"
          onChange={(e) => e.target.files?.[0] && onAudioFile(e.target.files[0])}
          style={{ display: "none" }}
          data-testid="ads-music-input"
        />
      </label>
      {audioErr && (
        <div style={{ marginTop: 6, color: "#fca5a5", fontSize: 11 }} data-testid="ads-music-err">
          {audioErr}
        </div>
      )}

      <div className="ads-panel-title" style={{ fontSize: 10, marginTop: 14, marginBottom: 8, display: "flex", alignItems: "center", gap: 6 }}>
        <Zap size={11} /> Motion Presets — one-click choreography
      </div>
      <div className="ads-motion-grid" data-testid="ads-motion-presets">
        {MOTION_PRESETS.map((p) => (
          <button key={p.id}
            onClick={() => applyPreset(p)}
            disabled={objCount === 0}
            className="ads-motion-card"
            title={p.desc}
            data-testid={`ads-motion-preset-${p.id}`}
            style={{ background: p.gradient, opacity: objCount === 0 ? 0.4 : 1 }}
          >
            <div className="ads-motion-label">{p.label}</div>
            <div className="ads-motion-dur">{(p.duration_ms / 1000).toFixed(1)}s</div>
          </button>
        ))}
      </div>
      <div style={{ fontSize: 10, color: "var(--ads-text-dim)", marginBottom: 14, fontStyle: "italic" }}>
        Hover a preset to see when to use it.
      </div>

      <div className="ads-prop-row">
        <label className="ads-prop-label">Total duration (ms)</label>
        <input type="number" className="ads-prop-input"
          min={500} max={30000} step={100}
          value={duration}
          onChange={(e) => setDuration(parseInt(e.target.value) || 5000)}
          data-testid="ads-anim-duration" />
      </div>

      <div style={{ fontSize: 11, color: "var(--ads-text-dim)", marginBottom: 8 }}>
        {selectedIdx == null
          ? `Select an object to add animation (${objCount} on canvas).`
          : `Adding to object #${selectedIdx + 1}`}
      </div>

      {/* Interactive Timeline */}
      {animations.length > 0 && (
        <div className="ads-timeline" data-testid="ads-anim-timeline">
          <div className="ads-timeline-head">
            <span>0s</span>
            <span>{(duration / 2 / 1000).toFixed(1)}s</span>
            <span>{(duration / 1000).toFixed(1)}s</span>
          </div>
          <div className="ads-timeline-track" ref={trackRef}>
            {animations.map((a, i) => {
              const left = (a.start_at / duration) * 100;
              const w = Math.max(4, (a.duration / duration) * 100);
              const isSelected = selectedAnim === i;
              return (
                <div key={i}
                  className={`ads-timeline-bar ${isSelected ? "selected" : ""} ${isExit(a.type) ? "exit" : ""}`}
                  style={{ left: `${left}%`, width: `${w}%`, background: getAnimColor(a.type) }}
                  title={`${a.type} · obj #${a.object_index + 1} · ${a.start_at}ms → ${a.start_at + a.duration}ms`}
                  data-testid={`ads-anim-bar-${i}`}
                  onMouseDown={(e) => onBarMouseDown(e, i, "move")}
                >
                  <span style={{ pointerEvents: "none" }}>#{a.object_index + 1}</span>
                  <div className="ads-timeline-resize"
                    onMouseDown={(e) => onBarMouseDown(e, i, "resize")}
                    data-testid={`ads-anim-bar-resize-${i}`}
                  />
                </div>
              );
            })}
          </div>
          <div className="ads-timeline-hint">
            Drag bars to reposition · drag right edge to resize duration
          </div>
        </div>
      )}

      {/* Entry animation types */}
      <div className="ads-panel-title" style={{ fontSize: 10, marginTop: 6, marginBottom: 6 }}>Entry</div>
      <div className="ads-filter-row" style={{ marginBottom: 10 }}>
        {ENTRY_TYPES.map((t) => (
          <button key={t.id} className="ads-chip"
            disabled={selectedIdx == null}
            onClick={() => addAnim(t.id)}
            data-testid={`ads-anim-add-${t.id}`}
            style={{ opacity: selectedIdx == null ? 0.4 : 1 }}
          ><Plus size={11} /> {t.label}</button>
        ))}
      </div>

      {/* Exit animation types */}
      <div className="ads-panel-title" style={{ fontSize: 10, marginBottom: 6 }}>Exit</div>
      <div className="ads-filter-row" style={{ marginBottom: 14 }}>
        {EXIT_TYPES.map((t) => (
          <button key={t.id} className="ads-chip"
            disabled={selectedIdx == null}
            onClick={() => addAnim(t.id)}
            data-testid={`ads-anim-add-${t.id}`}
            style={{ opacity: selectedIdx == null ? 0.4 : 1 }}
          ><Plus size={11} /> {t.label}</button>
        ))}
      </div>

      <div style={{ display: "grid", gap: 8 }} data-testid="ads-anim-list">
        {animations.length === 0 && (
          <div style={{ fontSize: 12, color: "var(--ads-text-dim)", fontStyle: "italic" }}>
            No animations yet. Select an object and click a type above.
          </div>
        )}
        {animations.map((a, i) => {
          const meta = [...ENTRY_TYPES, ...EXIT_TYPES].find((t) => t.id === a.type);
          const isSelected = selectedAnim === i;
          return (
            <div key={i}
              onClick={() => setSelectedAnim(i)}
              style={{
                background: "var(--ads-bg)",
                border: `1px solid ${isSelected ? "var(--ads-accent)" : "var(--ads-border)"}`,
                borderRadius: 10, padding: 10, cursor: "pointer",
              }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <div style={{ fontSize: 12, fontWeight: 700 }}>
                  <Sparkles size={12} style={{ marginRight: 4, color: getAnimColor(a.type) }} />
                  {meta?.label || a.type}
                  <span style={{ color: "var(--ads-text-dim)", fontWeight: 400, marginLeft: 6 }}>
                    · obj #{a.object_index + 1}
                  </span>
                </div>
                <button className="ads-btn ads-btn-ghost" style={{ padding: "3px 6px", color: "#f87171" }}
                  onClick={(e) => { e.stopPropagation(); removeAnim(i); }} data-testid={`ads-anim-remove-${i}`}>
                  <Trash2 size={11} />
                </button>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
                <label style={{ fontSize: 10, color: "var(--ads-text-dim)" }}>
                  Start (ms)
                  <input type="number" className="ads-prop-input" style={{ marginTop: 2 }}
                    value={a.start_at} step={100}
                    onChange={(e) => updateAnim(i, { start_at: parseInt(e.target.value) || 0 })} />
                </label>
                <label style={{ fontSize: 10, color: "var(--ads-text-dim)" }}>
                  Duration (ms)
                  <input type="number" className="ads-prop-input" style={{ marginTop: 2 }}
                    value={a.duration} step={100}
                    onChange={(e) => updateAnim(i, { duration: parseInt(e.target.value) || 600 })} />
                </label>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
